/**
 * Love Analyzer — Sprint 16b
 *
 * Two-pass LLM pipeline for entity detection:
 *   Pass 1: Source Summarizer (fast tier, parallel per-source)
 *   Pass 2: Entity + Opportunity Analyzer (standard tier, single call)
 *
 * Orchestrates: aggregation → sanitization → Pass 1 → Pass 2 → validation → scoring → persistence
 */
import { makeLoveEntityId, emptySourceBreakdown, emptyProxyBreakdown } from '../types/love-entity';
import { composeEntities } from './entity-composer';
import { getIkigaiContextForAgent } from '../storage/profile-store';
import { aggregateSourceData, aggregateEmailRelationships, } from './data-aggregator';
import { sanitizeForLLM } from './data-sanitizer';
import { buildPass1Prompt, buildPass2Prompt, buildPass2RetryPrompt } from './prompts';
import { parseAndValidatePass2 } from './pass2-schema';
import { parseAndValidatePass1, formatSignalsForPass2 } from './pass1-schema';
import { buildAuditPrompt, parseAuditResponse, applyAuditAdjustments, emptyAuditMetrics, } from './self-audit';
import { createEmptyDetectionState, storeSummary, updateWatermark, markRunCompleted, getAvailableSummaries, isFirstOrMigrationRun, } from './detection-state';
import { initDualConfidence, confirmDualConfidence, decayDualConfidence, applyFeedbackHistory, } from './love-scorer';
import { recordIkigaiRouteLocalOutcome as recordRouteLocalOutcome, } from './route-local-outcome';
import { NS } from '@ownyou/shared-types';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export { recordIkigaiRouteLocalOutcome, } from './route-local-outcome';
function getSourceSummaryModel(modelPrefs) {
    return modelPrefs.sourceSummary ?? modelPrefs.fast;
}
function getEntitySynthesisModel(modelPrefs) {
    return modelPrefs.entitySynthesis ?? modelPrefs.standard;
}
function getSelfAuditModel(modelPrefs) {
    return modelPrefs.selfAudit ?? modelPrefs.fast;
}
/** Custom error for skippable pipeline failures */
export class PipelineSkipError extends Error {
    reason;
    constructor(message, reason) {
        super(message);
        this.reason = reason;
        this.name = 'PipelineSkipError';
    }
}
// ---------------------------------------------------------------------------
// Pipeline cost estimation
// ---------------------------------------------------------------------------
/** Default cost estimates (Anthropic Haiku/Sonnet as of 2026-03). Override via BudgetManager. */
const DEFAULT_COST_PER_1K_TOKENS = {
    fast: { input: 0.00025, output: 0.00125 },
    standard: { input: 0.003, output: 0.015 },
};
export function estimatePipelineCost(sourcesToProcess, _modelPrefs, costRates = DEFAULT_COST_PER_1K_TOKENS) {
    // Pass 1: ~2K tokens per source
    const pass1Cost = sourcesToProcess.length * 2 * costRates.fast.input +
        sourcesToProcess.length * 0.5 * costRates.fast.output;
    // Pass 2: ~8K input, ~4K output
    const pass2Cost = 8 * costRates.standard.input + 4 * costRates.standard.output;
    return pass1Cost + pass2Cost;
}
async function runPass1(source, aggregatedData, privacyTier, llm, modelPrefs, tracer) {
    let routeLocalOutcome;
    try {
        tracer?.startStep('pass1', {
            source,
            inputPreview: aggregatedData.slice(0, 500),
        });
        // Sanitize based on privacy tier
        const sanitizedData = sanitizeForLLM(aggregatedData, privacyTier, source);
        const prompt = buildPass1Prompt(source, sanitizedData);
        const llmStart = Date.now();
        const result = await Promise.race([
            llm.complete(prompt, {
                model: getSourceSummaryModel(modelPrefs),
                operation: 'sourceSummary',
            }),
            new Promise((_, reject) => setTimeout(() => reject(new PipelineSkipError(`Pass 1 timeout for ${source}`, 'timeout')), 30_000)),
        ]);
        const rawText = result.text.trim();
        const inputTokens = result.usage?.inputTokens ?? 0;
        const outputTokens = result.usage?.outputTokens ?? 0;
        // Try to parse structured JSON output (Sprint 17c)
        const structured = parseAndValidatePass1(rawText);
        if (structured && structured.signals.length > 0) {
            // Format as ranked signal table for Pass 2
            const formattedSummary = formatSignalsForPass2(structured.signals, structured.underrepresentedSignals);
            console.log(`[LoveAnalyzer] Pass 1 ${source}: ${structured.signals.length} structured signals (intent levels: ${structured.signals.map(s => s.intentLevel).join(',')})`);
            tracer?.endStep({
                outputItemCount: structured.signals.length,
                outputSummary: { signals: structured.signals.length, source },
                llm: {
                    model: getSourceSummaryModel(modelPrefs) ?? 'unknown',
                    provider: '',
                    promptTokens: inputTokens,
                    completionTokens: outputTokens,
                    costUsd: 0, // computed later
                    latencyMs: Date.now() - llmStart,
                    promptPreview: prompt,
                    responsePreview: rawText,
                },
            });
            routeLocalOutcome = {
                operation: 'sourceSummary',
                status: 'success',
                result: 'success',
            };
            return {
                summary: formattedSummary,
                inputTokens,
                outputTokens,
                structuredSignals: structured.signals,
                underrepresentedSignals: structured.underrepresentedSignals,
            };
        }
        // Fallback: use raw text if JSON parsing fails (no crash)
        console.log(`[LoveAnalyzer] Pass 1 ${source}: fallback to raw text (structured parse failed)`);
        tracer?.endStep({
            outputSummary: { fallback: 'raw_text', source },
            llm: {
                model: getSourceSummaryModel(modelPrefs) ?? 'unknown',
                provider: '',
                promptTokens: inputTokens,
                completionTokens: outputTokens,
                costUsd: 0,
                latencyMs: Date.now() - llmStart,
                promptPreview: prompt,
                responsePreview: rawText,
                parseErrors: ['Structured JSON parse failed — using raw text'],
            },
        });
        routeLocalOutcome = {
            operation: 'sourceSummary',
            status: 'parse_failed',
            result: 'fallback',
            errorCode: 'parse_failed',
        };
        return {
            summary: rawText,
            inputTokens,
            outputTokens,
        };
    }
    finally {
        recordRouteLocalOutcome(llm, routeLocalOutcome);
    }
}
// ---------------------------------------------------------------------------
// Pass 2: Entity + Opportunity Analyzer
// ---------------------------------------------------------------------------
async function runPass2(state, freshSources, existingEntities, llm, modelPrefs, feedbackSummary, emailRelationships, profileContext) {
    const allSources = ['financial', 'calendar', 'email', 'browsing'];
    const summaries = allSources.map(source => {
        const stored = state.sourceSummaries[source];
        return {
            source,
            summary: stored?.summary ?? null,
            updatedAt: stored?.generatedAt ?? null,
            itemCount: stored?.itemCount ?? null,
            isFresh: freshSources.has(source),
        };
    });
    // Check if we have any summaries at all
    const hasSummaries = summaries.some(s => s.summary !== null);
    if (!hasSummaries) {
        throw new PipelineSkipError('No source summaries available', 'no_data');
    }
    // Quality gate: filter out garbage entities from prior broken runs
    // to avoid polluting the prompt with noise (URLs, fragments, uncategorized junk)
    const qualityEntities = existingEntities.filter(e => {
        // Must have a real category (not "Uncategorized" or empty)
        if (!e.category || e.category === 'Uncategorized')
            return false;
        // Name must be a real entity (not a URL, fragment, or raw data artifact)
        if (e.name.includes('://') || e.name.includes('.com') || e.name.includes('.co.uk'))
            return false;
        if (e.name.length < 3 || e.name.length > 80)
            return false;
        // Filter obvious garbage patterns: parenthetical fragments, colons at end, etc.
        if (/^[\(\)\[\]:,\.\s]+$/.test(e.name))
            return false;
        if (e.name.endsWith(':') || e.name.endsWith(')') || e.name.startsWith('('))
            return false;
        // Must have at least one non-zero ikigai score
        const ik = e.ikigaiRelevance;
        if (ik &&
            (ik.love ?? 0) === 0 &&
            (ik.goodAt ?? 0) === 0 &&
            (ik.worldNeeds ?? 0) === 0 &&
            (ik.paidFor ?? 0) === 0)
            return false;
        return true;
    });
    if (qualityEntities.length < existingEntities.length) {
        console.log(`[LoveAnalyzer] Quality gate: filtered ${existingEntities.length - qualityEntities.length} garbage entities (keeping ${qualityEntities.length})`);
    }
    const prompt = buildPass2Prompt({
        summaries,
        existingEntities: qualityEntities,
        currentDate: new Date().toISOString().split('T')[0],
        feedbackSummary,
        emailRelationships,
        profileContext,
    });
    // First attempt — request 16K output tokens to avoid truncation on complex analyses
    const result = await Promise.race([
        llm.complete(prompt, {
            model: getEntitySynthesisModel(modelPrefs),
            operation: 'entitySynthesis',
            maxTokens: 16384,
        }),
        new Promise((_, reject) => setTimeout(() => reject(new PipelineSkipError('Pass 2 timeout', 'timeout')), 120_000)),
    ]);
    let parsed = parseAndValidatePass2(result.text);
    let totalInputTokens = result.usage?.inputTokens ?? 0;
    let totalOutputTokens = result.usage?.outputTokens ?? 0;
    let parseAttempts = 1;
    // Retry once if no valid entities
    if (parsed.entities.length === 0 && parsed.errors.length > 0) {
        parseAttempts = 2;
        const retryPrompt = buildPass2RetryPrompt(prompt, parsed.errors.join('\n'));
        try {
            const retryResult = await Promise.race([
                llm.complete(retryPrompt, {
                    model: getEntitySynthesisModel(modelPrefs),
                    operation: 'entitySynthesis',
                    maxTokens: 16384,
                }),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Retry timeout')), 120_000)),
            ]);
            parsed = parseAndValidatePass2(retryResult.text);
            totalInputTokens += retryResult.usage?.inputTokens ?? 0;
            totalOutputTokens += retryResult.usage?.outputTokens ?? 0;
        }
        catch (retryError) {
            console.warn('[LoveAnalyzer] Pass 2 retry failed:', retryError instanceof Error ? retryError.message : retryError);
        }
    }
    recordRouteLocalOutcome(llm, {
        operation: 'entitySynthesis',
        status: parsed.errors.length > 0 ? 'parse_failed' : 'success',
        result: parsed.errors.length > 0 ? 'fallback' : 'success',
        errorCode: parsed.errors.length > 0 ? 'parse_failed' : undefined,
    });
    const opportunities = parsed.opportunities.map(o => ({
        ...o,
        createdAt: Date.now(),
        status: 'pending',
    }));
    return {
        entities: parsed.entities,
        opportunities,
        inputTokens: totalInputTokens,
        outputTokens: totalOutputTokens,
        parseAttempts,
        promptText: prompt,
        responseText: result.text,
    };
}
// ---------------------------------------------------------------------------
// Entity conversion: Pass2Entity → LoveEntity
// ---------------------------------------------------------------------------
function pass2EntityToLoveEntity(raw, pass1Model, pass2Model, runId) {
    const now = Date.now();
    const signals = raw.signals.map(s => ({
        source: s.source,
        proxy: s.proxy,
        summary: s.summary,
        strength: s.strength,
        timestamp: now,
    }));
    const sourceBreakdown = emptySourceBreakdown();
    const proxyBreakdown = emptyProxyBreakdown();
    for (const s of signals) {
        sourceBreakdown[s.source]++;
        proxyBreakdown[s.proxy]++;
    }
    return {
        id: makeLoveEntityId(raw.type, raw.name),
        type: raw.type,
        name: raw.name,
        category: raw.category,
        subcategories: raw.subcategories,
        confidence: raw.confidence,
        lastValidatedAt: now,
        signals,
        sourceBreakdown,
        proxyBreakdown,
        firstSeenAt: now,
        lastSignalAt: now,
        trend: raw.status === 'declining'
            ? 'declining'
            : raw.status === 'new'
                ? 'new'
                : raw.status === 'confirmed'
                    ? 'stable'
                    : 'growing',
        actionableContext: {
            location: raw.actionableContext.location,
            budget: raw.actionableContext.budget,
            socialContext: raw.actionableContext.socialContext,
            timing: raw.actionableContext.timing,
            frequency: raw.actionableContext.frequency,
            constraints: raw.actionableContext.constraints,
            suggestedActions: raw.actionableContext.suggestedActions ?? [],
            relatedEntityIds: [],
        },
        reasoning: raw.reasoning,
        proxyEvidence: raw.proxies,
        nature: raw.nature,
        ikigaiRelevance: raw.ikigaiRelevance,
        stability: raw.stability,
        temporalPattern: raw.temporalPattern,
        deadline: raw.deadline,
        urgencyReason: raw.urgencyReason,
        pass1Model,
        pass2Model,
        detectionRunId: runId,
    };
}
/**
 * Merge a new LLM-detected entity with an existing entity.
 * Preserves historical signals and firstSeenAt.
 */
function mergeWithExisting(newEntity, existing) {
    // Merge signals: new first, then existing, capped at 20
    const mergedSignals = [...newEntity.signals, ...existing.signals]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 20);
    const sourceBreakdown = emptySourceBreakdown();
    const proxyBreakdown = emptyProxyBreakdown();
    for (const s of mergedSignals) {
        sourceBreakdown[s.source]++;
        proxyBreakdown[s.proxy]++;
    }
    return {
        ...newEntity,
        firstSeenAt: existing.firstSeenAt,
        signals: mergedSignals,
        sourceBreakdown,
        proxyBreakdown,
    };
}
// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------
/**
 * Backfill entities from Sprint 16a with new fields.
 */
export function migrateExistingEntities(entities) {
    return entities.map(entity => ({
        ...entity,
        reasoning: entity.reasoning ?? 'Migrated from prior analysis — will update on next run',
        proxyEvidence: entity.proxyEvidence ?? {},
        ikigaiRelevance: entity.ikigaiRelevance ?? { love: 0.5, goodAt: 0, worldNeeds: 0, paidFor: 0 },
        stability: entity.stability ?? 0.5,
        nature: entity.nature ?? undefined,
        temporalPattern: entity.temporalPattern ?? undefined,
        pass1Model: entity.pass1Model ?? 'deterministic-v16a',
        pass2Model: entity.pass2Model ?? 'deterministic-v16a',
        detectionRunId: entity.detectionRunId ?? 'migration-0',
        actionableContext: {
            ...entity.actionableContext,
            suggestedActions: entity.actionableContext?.suggestedActions ?? [],
            relatedEntityIds: entity.actionableContext?.relatedEntityIds ?? [],
        },
    }));
}
/**
 * Derive Ikigai dimension scores from entity ikigaiRelevance.
 */
export function deriveDimensionScores(entities) {
    const dimScore = (dim) => {
        const relevant = entities.filter(e => (e.ikigaiRelevance?.[dim] ?? 0) > 0.1);
        if (relevant.length === 0)
            return 0;
        return (relevant.reduce((sum, e) => sum + e.confidence * (e.ikigaiRelevance?.[dim] ?? 0), 0) /
            relevant.length);
    };
    return {
        passion: dimScore('love'),
        mission: dimScore('worldNeeds'),
        vocation: dimScore('goodAt'),
        profession: dimScore('paidFor'),
    };
}
// ---------------------------------------------------------------------------
// Fuzzy name matching (used in merge + intra-run dedup)
// ---------------------------------------------------------------------------
function normalizeEntityName(name) {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}
function entityWords(name) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .split(/\s+/)
        .filter(w => w.length > 2);
}
/**
 * Check if two entity names refer to the same concept using multiple strategies.
 * When categories match, the threshold is lower (any shared word suffices).
 */
function fuzzyNameMatchStatic(nameA, nameB, categoryA, categoryB) {
    const normA = normalizeEntityName(nameA);
    const normB = normalizeEntityName(nameB);
    // Strategy 1: substring containment
    if (normA.includes(normB) || normB.includes(normA))
        return true;
    // Strategy 2: long common prefix
    if (normA.length > 10 && normB.length > 10 && normA.slice(0, 12) === normB.slice(0, 12))
        return true;
    // Strategy 3: word overlap
    const wordsA = entityWords(nameA);
    const wordsB = entityWords(nameB);
    if (wordsA.length > 0 && wordsB.length > 0) {
        const shared = wordsA.filter(w => wordsB.some(ew => ew.includes(w) || w.includes(ew)));
        const overlapRatio = shared.length / Math.min(wordsA.length, wordsB.length);
        // Same or overlapping category = lower threshold (any shared significant word)
        const catA = categoryA?.toLowerCase() ?? '';
        const catB = categoryB?.toLowerCase() ?? '';
        const sameCategory = categoryA && categoryB && (catA === catB || catA.includes(catB) || catB.includes(catA));
        if (sameCategory && shared.length >= 1)
            return true;
        // Different/unknown category = stricter threshold
        if (overlapRatio >= 0.5 && shared.length >= 1)
            return true;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Main pipeline
// ---------------------------------------------------------------------------
function generateRunId() {
    return `run-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
/**
 * Get privacy tier for a model.
 */
export function getPrivacyTier(modelId) {
    if (!modelId)
        return 'cloud-standard';
    const lower = modelId.toLowerCase();
    if (lower.includes('ollama') || lower.includes('local') || lower.includes('llama.cpp')) {
        return 'local';
    }
    if (lower.includes('anthropic') || lower.includes('claude') || lower.includes('google')) {
        return 'cloud-zdr';
    }
    return 'cloud-standard';
}
/**
 * Run the full entity detection pipeline.
 *
 * @param sourceData - Raw data from all sources
 * @param existingEntities - Current entities from Store
 * @param existingState - Detection state from prior runs (null = first run)
 * @param llm - LLM client
 * @param modelPrefs - User's model preferences
 * @param budget - Optional budget manager
 * @param changedSources - Which sources triggered this run (for incremental)
 * @param feedbackByEntity - Sprint 17c: feedback history grouped by entity ID
 * @param tracer - Optional pipeline tracer for transparency
 */
export async function runDetectionPipeline(sourceData, existingEntities, existingState, llm, modelPrefs, budget, changedSources, feedbackByEntity, tracer, userId, store) {
    const startTime = Date.now();
    const runId = generateRunId();
    const state = existingState ?? createEmptyDetectionState();
    const privacyTier = modelPrefs.privacyTier ?? getPrivacyTier(getSourceSummaryModel(modelPrefs));
    console.log(`[LoveAnalyzer] Source summary model: ${getSourceSummaryModel(modelPrefs)}, Entity synthesis model: ${getEntitySynthesisModel(modelPrefs)}, Privacy tier: ${privacyTier}`);
    // Migrate existing entities if needed
    const migratedEntities = migrateExistingEntities(existingEntities);
    const isFirstRun = isFirstOrMigrationRun(existingState, migratedEntities.map(e => e.detectionRunId));
    // Determine which sources to process
    let sourcesToProcess;
    if (isFirstRun) {
        // Full refresh: process all sources with data
        sourcesToProcess = ['financial', 'calendar', 'email', 'browsing'].filter(s => {
            const data = sourceData[s];
            return data && (Array.isArray(data) ? data.length > 0 : true);
        });
    }
    else if (changedSources && changedSources.length > 0) {
        sourcesToProcess = changedSources.filter(s => {
            const data = sourceData[s];
            return data && (Array.isArray(data) ? data.length > 0 : true);
        });
    }
    else {
        // No sources specified and not first run: skip
        throw new PipelineSkipError('No new data to process', 'no_data');
    }
    if (sourcesToProcess.length === 0) {
        throw new PipelineSkipError('No source data available', 'no_data');
    }
    // Trace: data loading
    tracer?.startStep('data_loading', {
        inputItemCount: Object.values(sourceData).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0),
    });
    tracer?.endStep({
        outputItemCount: sourcesToProcess.length,
        outputSummary: Object.fromEntries(sourcesToProcess.map(s => [
            s,
            Array.isArray(sourceData[s]) ? sourceData[s].length : 0,
        ])),
    });
    // Budget check
    if (budget) {
        const estimatedCost = estimatePipelineCost(sourcesToProcess, modelPrefs);
        const canSpend = await budget.canSpend('ikigai', estimatedCost);
        if (!canSpend) {
            throw new PipelineSkipError('Daily budget exceeded', 'budget');
        }
    }
    // Pre-aggregate all source data
    tracer?.startStep('aggregation', {
        inputItemCount: sourcesToProcess.length,
    });
    const contextTokens = modelPrefs.modelContextTokens ?? 128_000;
    const aggregated = aggregateSourceData(sourceData, contextTokens);
    tracer?.endStep({
        outputItemCount: Object.keys(aggregated).filter(k => aggregated[k]).length,
        outputSummary: Object.fromEntries(Object.entries(aggregated)
            .filter(([, v]) => v)
            .map(([k, v]) => [k, typeof v === 'string' ? v.length : 0])),
    });
    // Pass 1: Summarize sources in parallel
    let pass1InputTokens = 0;
    let pass1OutputTokens = 0;
    const freshSources = new Set();
    let updatedState = { ...state };
    const pass1Results = await Promise.allSettled(sourcesToProcess.map(async (source) => {
        const data = aggregated[source];
        if (!data)
            return null;
        const result = await runPass1(source, data, privacyTier, llm, modelPrefs, tracer);
        return { source, ...result };
    }));
    for (const result of pass1Results) {
        if (result.status === 'fulfilled' && result.value) {
            const { source, summary, inputTokens, outputTokens } = result.value;
            freshSources.add(source);
            pass1InputTokens += inputTokens;
            pass1OutputTokens += outputTokens;
            // Store summary and update watermark
            const sourceDataArr = sourceData[source];
            const itemCount = Array.isArray(sourceDataArr) ? sourceDataArr.length : 0;
            updatedState = storeSummary(updatedState, source, summary, getSourceSummaryModel(modelPrefs) ?? 'unknown', itemCount);
            updatedState = updateWatermark(updatedState, source, Date.now());
        }
        else if (result.status === 'rejected') {
            console.error(`[LoveAnalyzer] Pass 1 failed:`, result.reason?.message ?? result.reason);
        }
    }
    // Check if we got any fresh summaries (or have stored ones)
    const allSummaries = getAvailableSummaries(updatedState);
    if (allSummaries.length === 0) {
        throw new PipelineSkipError('No summaries produced', 'no_data');
    }
    // Compute email relationship data (bypasses Pass 1 lossy compression for person entities)
    let emailRelationships = '';
    if (sourceData.email && sourceData.email.length > 0) {
        const rawRelationships = aggregateEmailRelationships(sourceData.email);
        if (rawRelationships) {
            emailRelationships = sanitizeForLLM(rawRelationships, privacyTier, 'email');
            console.log(`[LoveAnalyzer] Email relationships: ${rawRelationships.split('\n').length} personal senders extracted`);
        }
    }
    // Sprint 17c: Build feedback summary for Pass 2 prompt
    let feedbackSummary;
    if (feedbackByEntity && feedbackByEntity.size > 0) {
        const lines = [];
        for (const [entityId, feedbacks] of feedbackByEntity) {
            const accepted = feedbacks.filter(f => f.type === 'mission_accepted' || f.type === 'confirm').length;
            const rejected = feedbacks.filter(f => f.type === 'mission_rejected' || f.type === 'dispute').length;
            if (accepted > 0 || rejected > 0) {
                const parts = [];
                if (accepted > 0)
                    parts.push(`${accepted} accepted/confirmed`);
                if (rejected > 0)
                    parts.push(`${rejected} dismissed/disputed`);
                lines.push(`${entityId}: ${parts.join(', ')}`);
            }
        }
        if (lines.length > 0) {
            feedbackSummary = lines.join('\n');
        }
    }
    // Sprint 17g-3: Load unified profile for Pass 2
    let profileContext;
    if (userId && store) {
        try {
            const { UserProfileStore } = await import('../storage/user-profile-store');
            const profileStore = new UserProfileStore(store, userId);
            profileContext = await profileStore.toPromptContext();
        }
        catch (err) {
            console.warn('[LoveAnalyzer] Profile load failed (non-blocking):', err);
        }
    }
    // Pass 2: Cross-source entity analysis
    tracer?.startStep('pass2', {
        inputItemCount: allSummaries.length,
    });
    const pass2Start = Date.now();
    const pass2Result = await runPass2(updatedState, freshSources, migratedEntities, llm, modelPrefs, feedbackSummary, emailRelationships || undefined, profileContext);
    tracer?.endStep({
        outputItemCount: pass2Result.entities.length,
        outputSummary: {
            entities: pass2Result.entities.length,
            opportunities: pass2Result.opportunities.length,
            parseAttempts: pass2Result.parseAttempts,
        },
        llm: {
            model: getEntitySynthesisModel(modelPrefs) ?? 'unknown',
            provider: '',
            promptTokens: pass2Result.inputTokens,
            completionTokens: pass2Result.outputTokens,
            costUsd: 0,
            latencyMs: Date.now() - pass2Start,
            promptPreview: pass2Result.promptText,
            responsePreview: pass2Result.responseText,
            parseAttempts: pass2Result.parseAttempts,
        },
    });
    // Pass 2.5: Self-Audit Reflection (Sprint 17c)
    let auditMetrics = emptyAuditMetrics();
    let pass2Entities = pass2Result.entities;
    let allOpportunities = pass2Result.opportunities;
    let selfAuditOutcome;
    tracer?.startStep('self_audit');
    try {
        const auditStart = Date.now();
        const connectedSources = sourcesToProcess;
        const summaryText = allSummaries
            .map(s => `=== ${s.source} ===\n${updatedState.sourceSummaries[s.source]?.summary ?? ''}`)
            .join('\n\n');
        const auditPrompt = buildAuditPrompt(summaryText, pass2Entities, allOpportunities, connectedSources);
        const auditResult = await Promise.race([
            llm.complete(auditPrompt, { model: getSelfAuditModel(modelPrefs), operation: 'selfAudit' }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Audit timeout')), 15_000)),
        ]);
        const audit = parseAuditResponse(auditResult.text);
        if (audit) {
            auditMetrics = {
                auditRun: true,
                auditVerdict: audit.verdict,
                auditIssueCount: audit.issues.length,
                auditMissingOpportunities: audit.missingOpportunities.length,
                auditDurationMs: Date.now() - auditStart,
                auditModel: getSelfAuditModel(modelPrefs),
            };
            if (audit.verdict === 'issues_found') {
                const { adjustedEntities, additionalOpportunities } = applyAuditAdjustments(pass2Entities, allOpportunities, audit);
                pass2Entities = adjustedEntities;
                allOpportunities = [...allOpportunities, ...additionalOpportunities];
                console.log(`[LoveAnalyzer] Audit: ${audit.issues.length} issues found, ${additionalOpportunities.length} opportunities added`);
                tracer?.endStep({
                    outputSummary: {
                        verdict: 'issues_found',
                        issues: audit.issues.length,
                        addedOpportunities: additionalOpportunities.length,
                    },
                    llm: {
                        model: getSelfAuditModel(modelPrefs) ?? 'unknown',
                        provider: '',
                        promptTokens: auditResult.usage?.inputTokens ?? 0,
                        completionTokens: auditResult.usage?.outputTokens ?? 0,
                        costUsd: 0,
                        latencyMs: auditMetrics.auditDurationMs ?? 0,
                        promptPreview: auditPrompt,
                        responsePreview: auditResult.text,
                    },
                });
                selfAuditOutcome = {
                    operation: 'selfAudit',
                    status: 'success',
                    result: 'success',
                };
            }
            else {
                console.log('[LoveAnalyzer] Audit: pass — no issues found');
                tracer?.endStep({
                    outputSummary: { verdict: 'pass' },
                    llm: {
                        model: getSelfAuditModel(modelPrefs) ?? 'unknown',
                        provider: '',
                        promptTokens: auditResult.usage?.inputTokens ?? 0,
                        completionTokens: auditResult.usage?.outputTokens ?? 0,
                        costUsd: 0,
                        latencyMs: auditMetrics.auditDurationMs ?? 0,
                        promptPreview: auditPrompt,
                        responsePreview: auditResult.text,
                    },
                });
                selfAuditOutcome = {
                    operation: 'selfAudit',
                    status: 'success',
                    result: 'success',
                };
            }
        }
        else {
            auditMetrics = {
                ...emptyAuditMetrics(),
                auditRun: true,
                auditVerdict: 'skipped',
                auditDurationMs: Date.now() - auditStart,
            };
            console.warn('[LoveAnalyzer] Audit: parse failed, skipping');
            tracer?.endStep({ status: 'skipped', outputSummary: { verdict: 'parse_failed' } });
            selfAuditOutcome = {
                operation: 'selfAudit',
                status: 'parse_failed',
                result: 'skipped',
                errorCode: 'parse_failed',
            };
        }
    }
    catch (auditError) {
        auditMetrics = { ...emptyAuditMetrics(), auditVerdict: 'skipped' };
        console.warn('[LoveAnalyzer] Audit: skipped —', auditError instanceof Error ? auditError.message : auditError);
        tracer?.endStep({
            status: 'skipped',
            error: auditError instanceof Error ? auditError.message : String(auditError),
        });
        selfAuditOutcome = {
            operation: 'selfAudit',
            status: 'not_run',
            result: 'skipped',
            errorCode: 'provider_error',
        };
    }
    recordRouteLocalOutcome(llm, selfAuditOutcome);
    // Scoring: Convert Pass 2 entities to LoveEntities + dual confidence
    tracer?.startStep('scoring', { inputItemCount: pass2Entities.length });
    const pass1Model = getSourceSummaryModel(modelPrefs) ?? 'unknown';
    const pass2Model = getEntitySynthesisModel(modelPrefs) ?? 'unknown';
    const rawNewEntities = pass2Entities.map(raw => pass2EntityToLoveEntity(raw, pass1Model, pass2Model, runId));
    // Deduplicate within the new entities themselves (LLM may output near-duplicates)
    const newEntities = [];
    for (const ent of rawNewEntities) {
        const dupIndex = newEntities.findIndex(existing => existing.type === ent.type &&
            fuzzyNameMatchStatic(existing.name, ent.name, existing.category, ent.category));
        if (dupIndex >= 0) {
            // Keep the one with higher confidence, merge signals
            const kept = newEntities[dupIndex];
            console.log(`[LoveAnalyzer] Intra-run dedup: "${ent.name}" merged into "${kept.name}"`);
            newEntities[dupIndex] = mergeWithExisting(kept.confidence >= ent.confidence ? kept : ent, kept.confidence >= ent.confidence ? ent : kept);
        }
        else {
            newEntities.push(ent);
        }
    }
    // Merge with existing entities and apply dual confidence model (I5 fix)
    const existingMap = new Map(migratedEntities.map(e => [e.id, e]));
    const finalEntities = [];
    // Build lookup of existing entities for fuzzy matching
    const existingByNorm = new Map();
    for (const e of migratedEntities) {
        existingByNorm.set(normalizeEntityName(e.name), e);
    }
    for (const newEnt of newEntities) {
        // Try exact ID match first, then fuzzy name match
        let existing = existingMap.get(newEnt.id);
        if (!existing) {
            for (const [, existEnt] of existingByNorm) {
                if (fuzzyNameMatchStatic(newEnt.name, existEnt.name, newEnt.category, existEnt.category)) {
                    // Same type required for fuzzy match
                    if (existEnt.type === newEnt.type && existingMap.has(existEnt.id)) {
                        existing = existEnt;
                        console.log(`[LoveAnalyzer] Fuzzy match: "${newEnt.name}" → "${existEnt.name}"`);
                        break;
                    }
                }
            }
        }
        if (existing) {
            // Entity confirmed this run: apply dual confidence update
            const priorDual = initDualConfidence(existing.confidence);
            let updatedDual = confirmDualConfidence(priorDual, newEnt.confidence);
            // Sprint 17c: Apply feedback history
            const entityFeedback = feedbackByEntity?.get(newEnt.name) ?? feedbackByEntity?.get(newEnt.id) ?? [];
            if (entityFeedback.length > 0) {
                updatedDual = applyFeedbackHistory(updatedDual, entityFeedback);
            }
            const merged = mergeWithExisting(newEnt, existing);
            finalEntities.push({ ...merged, confidence: updatedDual.finalConfidence });
            existingMap.delete(existing.id); // Use existing.id for fuzzy match case
        }
        else {
            // New entity: initialize dual confidence
            let dual = initDualConfidence(newEnt.confidence);
            // Sprint 17c: Apply feedback history for new entities too
            const entityFeedback = feedbackByEntity?.get(newEnt.name) ?? feedbackByEntity?.get(newEnt.id) ?? [];
            if (entityFeedback.length > 0) {
                dual = applyFeedbackHistory(dual, entityFeedback);
            }
            finalEntities.push({ ...newEnt, confidence: dual.finalConfidence });
        }
    }
    // Entities NOT confirmed this run: apply temporal decay
    for (const remaining of existingMap.values()) {
        const daysSinceValidation = Math.max(0, (Date.now() - remaining.lastValidatedAt) / (1000 * 60 * 60 * 24));
        if (daysSinceValidation > 0) {
            const priorDual = initDualConfidence(remaining.confidence);
            const decayed = decayDualConfidence(priorDual, daysSinceValidation, remaining.stability ?? 0.5);
            finalEntities.push({ ...remaining, confidence: decayed.finalConfidence });
        }
        else {
            finalEntities.push(remaining);
        }
    }
    tracer?.endStep({
        outputItemCount: finalEntities.length,
        outputSummary: {
            total: finalEntities.length,
            new: newEntities.length,
            confirmed: pass2Entities.filter(e => e.status === 'confirmed').length,
            decayed: existingMap.size,
        },
    });
    // Persistence
    tracer?.startStep('persistence', { inputItemCount: finalEntities.length });
    // Update state
    updatedState = markRunCompleted(updatedState, runId, finalEntities.map(e => e.id));
    // Calculate cost
    const pass1Cost = (pass1InputTokens * DEFAULT_COST_PER_1K_TOKENS.fast.input +
        pass1OutputTokens * DEFAULT_COST_PER_1K_TOKENS.fast.output) /
        1000;
    const pass2Cost = (pass2Result.inputTokens * DEFAULT_COST_PER_1K_TOKENS.standard.input +
        pass2Result.outputTokens * DEFAULT_COST_PER_1K_TOKENS.standard.output) /
        1000;
    // Record budget spend
    if (budget) {
        await budget.recordSpend('ikigai', pass1Cost + pass2Cost);
    }
    tracer?.endStep({ outputItemCount: finalEntities.length });
    // Build entity diff for tracer
    if (tracer) {
        const existingIds = new Set(migratedEntities.map(e => e.id));
        const newIds = new Set(finalEntities.map(e => e.id));
        const added = finalEntities
            .filter(e => !existingIds.has(e.id))
            .map(e => ({
            entityId: e.id,
            entityName: e.name,
            entityType: e.type,
            newConfidence: e.confidence,
        }));
        const updated = finalEntities
            .filter(e => existingIds.has(e.id))
            .map(e => {
            const old = migratedEntities.find(o => o.id === e.id);
            return {
                entityId: e.id,
                entityName: e.name,
                entityType: e.type,
                oldConfidence: old?.confidence,
                newConfidence: e.confidence,
            };
        });
        const removed = migratedEntities
            .filter(e => !newIds.has(e.id))
            .map(e => ({
            entityId: e.id,
            entityName: e.name,
            entityType: e.type,
            oldConfidence: e.confidence,
            reason: 'decayed',
        }));
        tracer.setEntityDiff({
            added,
            updated,
            removed,
            unchanged: updated.filter(u => u.oldConfidence === u.newConfidence).length,
        });
    }
    // Sprint 17g-3: Populate CompositionContext from engagement events + feedback
    if (finalEntities.length >= 2) {
        let lovedMissions = [];
        let dismissedMissions = [];
        let completedMissions = [];
        if (userId && store) {
            let usedEngagementEvents = false;
            try {
                const engagementNs = NS.engagementEvents(userId);
                const eventData = await store.get(engagementNs, 'events');
                const events = eventData?.events ?? [];
                const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
                const recent = events.filter(e => e.timestamp > cutoff);
                if (recent.length > 0) {
                    usedEngagementEvents = true;
                    const missionsNs = NS.missionCards(userId);
                    const allMissions = await store.list(missionsNs, { limit: 200, offset: 0 });
                    const titleMap = new Map(allMissions.items.map(m => [m.id, m.title]));
                    const { POSITIVE_ACTIONS, NEGATIVE_ACTIONS } = await import('@ownyou/shared-types');
                    for (const event of recent) {
                        const title = titleMap.get(event.missionId) ?? event.missionId;
                        if (POSITIVE_ACTIONS.has(event.action)) {
                            if (event.action === 'completed')
                                completedMissions.push(title);
                            else
                                lovedMissions.push(title);
                        }
                        else if (NEGATIVE_ACTIONS.has(event.action)) {
                            dismissedMissions.push(title);
                        }
                    }
                }
            }
            catch {
                // Engagement events unavailable — fall through to feedbackByEntity
            }
            if (!usedEngagementEvents && feedbackByEntity) {
                for (const [entityId, feedbacks] of feedbackByEntity) {
                    if (feedbacks.some(f => f.type === 'mission_accepted' || f.type === 'confirm'))
                        lovedMissions.push(entityId);
                    if (feedbacks.some(f => f.type === 'mission_rejected' || f.type === 'dispute'))
                        dismissedMissions.push(entityId);
                }
            }
        }
        const compositionContext = {
            ikigaiSummary: userId && store ? await getIkigaiContextForAgent(userId, store) : undefined,
            lovedMissions: [...new Set(lovedMissions)].slice(0, 10),
            dismissedMissions: [...new Set(dismissedMissions)].slice(0, 10),
            completedMissions: [...new Set(completedMissions)].slice(0, 10),
        };
        const compositeOpps = await composeEntities(finalEntities, compositionContext, llm, modelPrefs);
        allOpportunities.push(...compositeOpps);
    }
    // Sprint 17g-1: Sync entities to unified profile + regenerate summary
    if (userId && store) {
        try {
            const { syncIkigaiToProfile } = await import('../bridges/ikigai-profile-bridge');
            const { UserProfileStore } = await import('../storage/user-profile-store');
            const profileStore = new UserProfileStore(store, userId);
            await syncIkigaiToProfile(finalEntities, feedbackByEntity, profileStore);
            await profileStore.regenerateSummary(llm, false, modelPrefs);
        }
        catch (err) {
            console.warn('[LoveAnalyzer] Profile sync/summary failed (non-blocking):', err);
        }
    }
    // Build metrics
    const metrics = {
        runId,
        timestamp: Date.now(),
        pass1Model,
        pass2Model,
        pass1TokensUsed: pass1InputTokens + pass1OutputTokens,
        pass2TokensUsed: pass2Result.inputTokens + pass2Result.outputTokens,
        pass1Cost,
        pass2Cost,
        entitiesProduced: finalEntities.length,
        entitiesConfirmed: pass2Entities.filter(e => e.status === 'confirmed').length,
        entitiesNew: pass2Entities.filter(e => e.status === 'new').length,
        entitiesDeclining: pass2Entities.filter(e => e.status === 'declining').length,
        opportunitiesGenerated: allOpportunities.length,
        signalsSummarized: sourcesToProcess.length,
        durationMs: Date.now() - startTime,
        privacyTier,
        parseAttempts: pass2Result.parseAttempts,
        // Sprint 17c: Audit metrics
        ...auditMetrics,
    };
    return {
        entities: finalEntities,
        opportunities: allOpportunities,
        metrics,
        state: updatedState,
    };
}
//# sourceMappingURL=love-analyzer.js.map